import type { SupportedLocale as SiteLocale } from "../../lib/localeRoutes.js";

export type SupportedLocale = SiteLocale;

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export interface SearchLocation extends Coordinates {
  provinceName: string;
  provinceSlug: string;
  displayName: string;
}

export type GeocodeRequest =
  | { kind: "address"; address: string; language: SupportedLocale }
  | {
      kind: "coordinates";
      latitude: number;
      longitude: number;
      language: SupportedLocale;
    };

export type GeocodeErrorCode =
  | "address_not_found"
  | "outside_turkey"
  | "provider_unavailable";

export type GeocodeApiResponse =
  | { ok: true; location: SearchLocation; attribution: "Google Maps" }
  | {
      ok: false;
      code: "invalid_request" | GeocodeErrorCode;
    };

export interface GoogleGeocoder {
  geocode(input: GeocodeRequest): Promise<SearchLocation>;
}

export class GeocodeError extends Error {
  constructor(public readonly code: GeocodeErrorCode) {
    super(code);
    this.name = "GeocodeError";
  }
}

export interface PharmacySource {
  id: string;
  name: string;
  url: string;
  attribution: string;
}

export interface DutyPharmacy extends Coordinates {
  providerId: string;
  sourceId: string;
  name: string;
  address: string;
  phone: string | null;
  province: string;
  district: string | null;
  dutyStart: string | null;
  dutyEnd: string | null;
}

export interface DutyPharmacySnapshot {
  sources: PharmacySource[];
  sourceDate: string;
  retrievedAt: string;
  pharmacies: DutyPharmacy[];
  omittedRecordCount?: number;
  unavailableProvinceCount?: number;
}

export interface DutyPharmacyRequestContext {
  now: Date;
  requestedDate: string;
}

export interface DutyPharmacyProvider {
  readonly id: string;
  getProvinceDutyPharmacies(
    provinceSlug: string,
    context?: DutyPharmacyRequestContext,
  ): Promise<DutyPharmacySnapshot>;
}

export type DutyPharmaciesApiResponse =
  | { ok: true; snapshot: DutyPharmacySnapshot }
  | {
      ok: false;
      code: "invalid_request" | "provider_unavailable" | "stale_data";
    };

export interface PharmacyWithDistance extends DutyPharmacy {
  distanceKm: number;
}

export interface NearbyPharmacyResult {
  radiusKm: 25 | 50;
  expanded: boolean;
  pharmacies: PharmacyWithDistance[];
}

export const PHARMACY_FINDER_ERROR_CODES = [
  "address_not_found",
  "outside_turkey",
  "geolocation_denied",
  "geolocation_unavailable",
  "geolocation_timeout",
  "invalid_request",
  "provider_unavailable",
  "stale_data",
  "no_nearby_pharmacies",
  "unsupported_province",
] as const;

export type PharmacyFinderErrorCode =
  (typeof PHARMACY_FINDER_ERROR_CODES)[number];

const PHARMACY_FINDER_ERROR_CODE_SET: ReadonlySet<string> = new Set(
  PHARMACY_FINDER_ERROR_CODES,
);

export function isPharmacyFinderErrorCode(
  value: unknown,
): value is PharmacyFinderErrorCode {
  return (
    typeof value === "string" && PHARMACY_FINDER_ERROR_CODE_SET.has(value)
  );
}
